import {
  UNIT_DEFS,
  UNIT_KINDS,
  unitDef,
  type PlayerId,
  type UnitKind,
} from "@dailywars/core/engine";

import { SectionHeader } from "#/components/SectionHeader";
import { Card } from "#/components/ui/card";
import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "#/components/ui/table";
import { cn } from "#/lib/utils";

import { CodexUnitIcon } from "./-codex.icons";

const matrixCellBorder = "border-r border-b border-border";
const matrixLabelCellSize = "box-border h-16 w-16 min-h-16 min-w-16 max-h-16 max-w-16 shrink-0 p-0";

const matrixTableClassName = cn(
  "w-full min-w-max table-fixed border-separate border-spacing-0",
  "[&_[data-slot=table-row]]:border-b-0",
  "[&_td:last-child]:border-r-0",
  "[&_thead_th:last-child]:border-r-0",
  "[&_tbody_tr:last-child_td]:border-b-0",
  "[&_tbody_tr:last-child_th]:border-b-0",
);

function damagePercent(attacker: UnitKind, defender: UnitKind): number {
  return UNIT_DEFS[attacker].damage[defender] ?? 0;
}

function cellShade(pct: number): string {
  if (pct <= 0) return "bg-background text-foreground";
  if (pct < 25) return "bg-rose-500/60 text-foreground";
  if (pct < 60) return "bg-amber-500/60 text-foreground";
  if (pct < 100) return "bg-emerald-500/40 text-foreground";
  return "bg-emerald-500/70 text-foreground";
}

function CodexDamageMatrixCell({ attacker, defender }: { attacker: UnitKind; defender: UnitKind }) {
  const armedTransportColumn = attacker === "landTransport";
  const pct = damagePercent(attacker, defender);
  const noDamage = pct <= 0;

  return (
    <TableCell
      className={cn(
        "h-16 min-h-16 px-1 py-0 text-center text-xs tabular-nums",
        matrixCellBorder,
        noDamage ? "bg-background text-foreground" : cellShade(pct),
      )}
    >
      {noDamage ? (
        armedTransportColumn ? (
          <>
            —<sup className="ml-px text-[0.6em] font-normal">1</sup>
          </>
        ) : (
          "—"
        )
      ) : (
        `${pct}%`
      )}
    </TableCell>
  );
}

function CodexDamageMatrixCornerCell() {
  const cornerLabelClassName =
    "absolute text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <TableHead className={cn("sticky left-0 z-30 bg-muted", matrixLabelCellSize, matrixCellBorder)}>
      <div className="relative size-full">
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full text-border"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <span className={cn(cornerLabelClassName, "bottom-[20%] left-[14%] rotate-45")}>Def</span>
        <span className={cn(cornerLabelClassName, "top-[20%] right-[14%] rotate-45")}>ATK</span>
      </div>
    </TableHead>
  );
}

function CodexDamageMatrixUnitLabel({
  kind,
  player = "p1",
  iconClassName,
}: {
  kind: UnitKind;
  player?: PlayerId;
  iconClassName?: string;
}) {
  const name = unitDef(kind).name;

  return (
    <Infotip>
      <InfotipTrigger aria-label={name} className="flex size-full items-center justify-center">
        <CodexUnitIcon kind={kind} player={player} className={cn("mx-0", iconClassName)} />
      </InfotipTrigger>
      <InfotipContent>{name}</InfotipContent>
    </Infotip>
  );
}

function CodexDamageMatrixDefenderCell({ kind }: { kind: UnitKind }) {
  return (
    <TableHead
      scope="row"
      className={cn(
        "sticky left-0 z-20 bg-muted text-center",
        matrixLabelCellSize,
        matrixCellBorder,
      )}
    >
      <CodexDamageMatrixUnitLabel kind={kind} player="p2" iconClassName="h-12 w-12" />
    </TableHead>
  );
}

function CodexDamageMatrixAttackerHeader({ kind }: { kind: UnitKind }) {
  return (
    <TableHead className={cn("h-16 min-h-16 bg-muted p-0 text-center", matrixCellBorder)}>
      <CodexDamageMatrixUnitLabel kind={kind} iconClassName="h-12 w-12" />
    </TableHead>
  );
}

export function CodexDamageMatrixTable() {
  return (
    <section className="flex flex-col gap-4">
      <SectionHeader>Damage matrix</SectionHeader>
      <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
        Damage values assume a fully healthy attacker (column) vs. a fully healthy defender (row),
        on a tile with no defense bonus. A dash means the attacker cannot damage that unit kind.
      </p>
      <InfotipProvider delay={300}>
        <Card className="overflow-x-auto py-0">
          <Table className={matrixTableClassName}>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow className="border-b-0 bg-muted hover:bg-muted">
                <CodexDamageMatrixCornerCell />
                {UNIT_KINDS.map((attacker) => (
                  <CodexDamageMatrixAttackerHeader key={attacker} kind={attacker} />
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {UNIT_KINDS.map((defender) => (
                <TableRow key={defender} className="border-b-0 hover:bg-transparent">
                  <CodexDamageMatrixDefenderCell kind={defender} />
                  {UNIT_KINDS.map((attacker) => (
                    <CodexDamageMatrixCell key={attacker} attacker={attacker} defender={defender} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </InfotipProvider>
      <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
        <sup>1</sup> Land Transports gains the attack of the carried foot soldier when loaded.
      </p>
    </section>
  );
}
